import React from 'react';
import IconSystem from 'icon-collection/icon-systems';
import type { PriceBoardImportResult } from 'api/types';
import { money, changeTable } from 'components/modules/dineops-module/masters/priceBoardMath';

/**
 * Price Board — Import preview (DineOps-Item-Pricing.md §3.1). Shows what the
 * file changes, Before → After, and the rows it could not use. Putting it on the
 * board makes the changes unsaved edits; an import never saves on its own.
 */

interface Props {
  result: PriceBoardImportResult | null;
  fileName: string;
  onClose: () => void;
  onApply: () => void;
}

const SHOWN = 300;
const shown = (n: number | null | undefined, menu: number, isMenu: boolean) =>
  (n == null ? (isMenu ? '' : `${money(menu)} ·`) : money(n));

const DineOpsPriceBoardImportModal: React.FC<Props> = ({ result, fileName, onClose, onApply }) => {
  if (!result) return null;
  const n = result.changes.length;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-lg" style={{ minHeight: 'auto', maxWidth: 820 }}>
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">Import prices — preview</h2>
          <button className="modal-close" onClick={onClose} aria-label="Close"><IconSystem.x size={20} /></button>
        </div>
        <div className="modal-content">
          <p style={{ marginTop: 0, fontSize: 14 }}>
            {fileName}: {result.rowsRead} row{result.rowsRead === 1 ? '' : 's'} read. <strong>{n}</strong> price{n === 1 ? '' : 's'} differ
            from the saved prices. Nothing is saved until you click <strong>Save</strong> on the board.
          </p>
          {n > 0 && (
            <div style={{ maxHeight: 340, overflow: 'auto', border: '1px solid var(--border-color)', borderRadius: 6 }}>
              <table style={changeTable.table}>
                <thead style={changeTable.head}>
                  <tr><th style={changeTable.th}>Code</th><th style={changeTable.th}>Dish</th><th style={changeTable.th}>Column</th><th style={{ ...changeTable.th, textAlign: 'right' }}>Before</th><th style={{ ...changeTable.th, textAlign: 'right' }}>After</th></tr>
                </thead>
                <tbody>
                  {result.changes.slice(0, SHOWN).map((c) => (
                    <tr key={`${c.itemId}|${c.outletId ?? 'menu'}`}>
                      <td style={changeTable.td}>{c.itemCode}</td>
                      <td style={changeTable.td}>{c.itemName}</td>
                      <td style={changeTable.td}>{c.column}</td>
                      <td style={{ ...changeTable.td, textAlign: 'right', color: 'var(--text-secondary)' }}>{shown(c.before, c.menuPrice, !c.outletId)}</td>
                      <td style={{ ...changeTable.td, textAlign: 'right', fontWeight: 600 }}>{shown(c.after, c.menuPrice, !c.outletId)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {n > SHOWN && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>Showing the first {SHOWN}. All {n} are applied.</div>}
          {n > 0 && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>A price with · follows the menu price.</div>}
          {result.warnings.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <div className="form-label">Not used</div>
              <ul className="form-error-message" style={{ margin: 0, paddingLeft: 18, maxHeight: 160, overflow: 'auto' }}>
                {result.warnings.map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            </div>
          )}
        </div>
        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={onApply} disabled={n === 0}>Put on the board</button>
        </div>
      </div>
    </>
  );
};

export default DineOpsPriceBoardImportModal;
